import React, { useState } from 'react';

import { CheckCircleOutlined, LinkOutlined, PlayCircleOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Card, Descriptions, Table, Tag, Button, Space, Modal, message, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, Link } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import { workOrderService } from '@services/operator/workOrderService';
import type { WorkOrderDetail, WorkOrderItem } from '@services/operator/workOrderService';
import { formatDateTime } from '@utils/dateTime';
import { WORK_ORDER_STATUS_COLORS, useWorkOrderStatusLabel } from '@constants/workOrder';

const { confirm } = Modal;

const WorkOrderShow: React.FC = () => {
  const initialWorkOrder = useLoaderData() as WorkOrderDetail;
  const [workOrder, setWorkOrder] = useState<WorkOrderDetail>(initialWorkOrder);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const workOrderStatusLabel = useWorkOrderStatusLabel();

  const [completeModalVisible, setCompleteModalVisible] = useState<boolean>(false);
  const [creatingPickingList, setCreatingPickingList] = useState<boolean>(false);

  const handleCreatePickingList = async () => {
    setCreatingPickingList(true);
    try {
      const res = await workOrderService.createPickingList(workOrder.id);
      if (res.success) {
        message.success(t('common.create_success'));
        setWorkOrder(res.data);
      } else {
        const errorKey = `operator.work_orders.errors.${res.message}`;
        const translatedError = t(errorKey) as string;
        message.error(translatedError !== errorKey ? translatedError : res.message);
      }
    } catch (e) {
      message.error(t('common.create_failed'));
    } finally {
      setCreatingPickingList(false);
    }
  };

  const handleConfirm = () => {
    confirm({
      title: t('operator.work_orders.list.actions.confirm'),
      content: t('operator.work_orders.list.actions.confirm_content'),
      onOk: async () => {
        try {
          const res = await workOrderService.confirm(workOrder.id);
          if (res.success) {
            message.success(t('common.update_success'));
            setWorkOrder(res.data);
          } else {
            // Translate API error code with interpolations
            const errorKey = `operator.work_orders.errors.${res.message}`;
            const translatedError = t(errorKey, res.interpolations || {}) as string;
            message.error(translatedError !== errorKey ? translatedError : res.message);
          }
        } catch (e) {
          message.error(t('common.update_failed'));
        }
      }
    });
  };

  const handleComplete = async () => {
      try {
          const res = await workOrderService.complete(workOrder.id);
           if (res.success) {
            message.success(t('common.update_success'));
            setWorkOrder(res.data);
            setCompleteModalVisible(false);
          } else {
            message.error(res.message);
          }
      } catch (e) {
           message.error(t('common.update_failed'));
      }
  };

  const itemColumns: ColumnsType<WorkOrderItem> = [
    {
      title: t('models.work_order_item.product'),
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text: string | null, record: WorkOrderItem) => (
          record.product_id ? (
            <Link to={`/products/${record.product_id}`} target="_blank">
                <div>{text} <LinkOutlined style={{ fontSize: 12 }} /></div>
                <div style={{ fontSize: 12, color: '#999' }}>{record.product_sku}</div>
            </Link>
          ) : (
            <div>
                <div>{text}</div>
                <div style={{ fontSize: 12, color: '#999' }}>{record.product_sku}</div>
            </div>
          )
      )
    },
    {
      title: t('models.work_order_item.inventory_type'),
      dataIndex: 'inventory_type_name',
      key: 'inventory_type_name',
    },
    {
      title: t('models.work_order_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
    },
    {
      title: t('models.work_order_item.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (text: string | null) => text ? formatDateTime(text, 'YYYY-MM-DD') : '-'
    },
    {
      title: t('operator.work_orders.list.columns.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('models.work_order_item.note'),
      dataIndex: 'note',
      key: 'note',
    }
  ];

  const inputItems = workOrder.items.filter((i) => i.role === 'input');
  const outputItems = workOrder.items.filter((i) => i.role === 'output');

  const headerTitle = (
    <Space>
      <span>{workOrder.name}</span>
      <Tag color={WORK_ORDER_STATUS_COLORS[workOrder.status] || 'default'}>
        {workOrderStatusLabel(workOrder.status)}
      </Tag>
    </Space>
  );

  const headerActions = (
    <>
      {(workOrder.status === 'draft' || workOrder.status === 'out_of_stock') && (
        <>
          <Button onClick={() => navigate(`/work_orders/${workOrder.id}/edit`)}>
            {t('common.button.edit')}
          </Button>
          <Button type="primary" onClick={handleConfirm} icon={<CheckCircleOutlined />}>
            {workOrder.status === 'out_of_stock'
              ? t('operator.work_orders.list.actions.retry_allocation')
              : t('operator.work_orders.list.actions.confirm')}
          </Button>
        </>
      )}
      {workOrder.status === 'allocated' && !workOrder.has_picking_list && (
        <Button
          type="primary"
          onClick={handleCreatePickingList}
          icon={<UnorderedListOutlined />}
          loading={creatingPickingList}
        >
          {t('operator.work_orders.list.actions.create_picking_list')}
        </Button>
      )}
      {workOrder.status === 'processing' && workOrder.picking_completed && (
        <Button type="primary" onClick={() => setCompleteModalVisible(true)} icon={<PlayCircleOutlined />}>
          {t('operator.work_orders.list.actions.complete')}
        </Button>
      )}
    </>
  );

  return (
    <div>
      <ListPageHeader
        items={['production', 'production.work_orders', { label: workOrder.name }]}
        title={headerTitle}
        actions={headerActions}
      />

      <Descriptions column={3} bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('models.work_order.barcode')}>{workOrder.barcode}</Descriptions.Item>
        <Descriptions.Item label={t('operator.work_orders.list.columns.merchant')}>{workOrder.merchant_name}</Descriptions.Item>
        <Descriptions.Item label={t('operator.work_orders.list.columns.warehouse')}>{workOrder.warehouse_name}</Descriptions.Item>
        <Descriptions.Item label={t('models.work_order.scheduled_at')}>{formatDateTime(workOrder.scheduled_at)}</Descriptions.Item>
        <Descriptions.Item label={t('models.work_order.created_at')}>{formatDateTime(workOrder.created_at)}</Descriptions.Item>
        <Descriptions.Item label={t('models.work_order.notes')}>{workOrder.notes || '-'}</Descriptions.Item>
      </Descriptions>

      {/* Picking List Info */}
      {workOrder.picking_lists && workOrder.picking_lists.length > 0 && (
        <Card title={t('operator.work_orders.detail.picking_list_title')} style={{ marginBottom: 16 }}>
          {workOrder.picking_lists.map((pl) => (
            <Alert
              key={pl.id}
              type={pl.status === 'done' ? 'success' : pl.status === 'processing' ? 'info' : 'warning'}
              showIcon
              message={
                <Space>
                  <span>{pl.name}</span>
                  <Tag color={
                    pl.status === 'done' ? 'success' :
                    pl.status === 'processing' ? 'processing' :
                    pl.status === 'ready' ? 'warning' : 'default'
                  }>
                    {t(`enums.picking_list.status.${pl.status}`)}
                  </Tag>
                  <Link to={`/picking_lists/${pl.id}`}>
                    {t('common.button.view')}
                  </Link>
                </Space>
              }
              description={
                !workOrder.picking_completed && (
                  <span style={{ color: '#999', fontSize: 12 }}>
                    {t('operator.work_orders.detail.picking_not_completed_hint')}
                  </span>
                )
              }
              style={{ marginBottom: 8 }}
            />
          ))}
        </Card>
      )}

      <Card
        title={
          <div>
            <div style={{ fontWeight: 500 }}>{t('operator.work_orders.detail.outputs_title')}</div>
            <div style={{ fontSize: 12, fontWeight: 400, color: '#999', marginTop: 4 }}>
              {t('operator.work_orders.detail.outputs_description')}
            </div>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
            dataSource={outputItems}
            columns={itemColumns}
            rowKey="id"
            pagination={false}
        />
      </Card>

      <Card
        title={
          <div>
            <div style={{ fontWeight: 500 }}>{t('operator.work_orders.detail.inputs_title')}</div>
            <div style={{ fontSize: 12, fontWeight: 400, color: '#999', marginTop: 4 }}>
              {t('operator.work_orders.detail.inputs_description')}
            </div>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
            dataSource={inputItems}
            columns={itemColumns}
            rowKey="id"
            pagination={false}
        />
      </Card>

      {/* Complete Modal */}
      <Modal
        title={t('operator.work_orders.list.actions.complete')}
        open={completeModalVisible}
        onOk={handleComplete}
        onCancel={() => setCompleteModalVisible(false)}
      >
        <p>{t('operator.work_orders.list.actions.complete_prompt')}</p>
      </Modal>
    </div>
  );
};

export default WorkOrderShow;
